import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History as HistoryIcon, Loader2, Search, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ACTION_META = {
  created: { label: "Created", badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  published: { label: "Published", badge: "bg-blue-100 text-blue-800 border-blue-200" },
  draft_saved: { label: "Draft Saved", badge: "bg-slate-100 text-slate-700 border-slate-200" },
  edited: { label: "Edited", badge: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  status_changed: { label: "Status Changed", badge: "bg-amber-100 text-amber-800 border-amber-200" },
  date_changed: { label: "Date Changed", badge: "bg-violet-100 text-violet-800 border-violet-200" },
  item_added: { label: "Item Added", badge: "bg-teal-100 text-teal-800 border-teal-200" },
  item_removed: { label: "Item Removed", badge: "bg-rose-100 text-rose-800 border-rose-200" },
  deleted: { label: "Deleted", badge: "bg-rose-100 text-rose-800 border-rose-200" },
};

const ACTION_OPTIONS = [
  "all", "created", "published", "draft_saved", "edited",
  "status_changed", "date_changed", "item_added", "item_removed", "deleted",
];

const inputCls =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}
function fmtDay(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function History() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [weeksLoaded, setWeeksLoaded] = useState(0);
  const [hasOlder, setHasOlder] = useState(false);
  const anchorRef = React.useRef(Date.now());
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [actor, setActor] = useState("all");
  const [factory, setFactory] = useState("all");
  const [action, setAction] = useState("all");

  const showFactoryFilter = user?.role === "owner";

  // Fetch a single 7-day window: n=0 is the most recent week, n=1 the week before, etc.
  const fetchWeek = useCallback(async (n) => {
    const anchor = anchorRef.current;
    const date_to = new Date(anchor - n * WEEK_MS).toISOString();
    const date_from = new Date(anchor - (n + 1) * WEEK_MS).toISOString();
    const { data } = await api.get("/history", { params: { date_from, date_to } });
    return data; // { records, has_older }
  }, [WEEK_MS]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWeek(0);
      setRecords(data.records);
      setHasOlder(data.has_older);
      setWeeksLoaded(1);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [fetchWeek]);

  const loadPrevious = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchWeek(weeksLoaded);
      setRecords((prev) => [...prev, ...data.records]);
      setHasOlder(data.has_older);
      setWeeksLoaded((n) => n + 1);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const actors = useMemo(() => {
    const seen = {};
    records.forEach((r) => { if (r.actor_id) seen[r.actor_id] = r.actor_name; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [records]);

  const factories = useMemo(() => {
    const seen = {};
    records.forEach((r) => { if (r.factory_id) seen[r.factory_id] = r.factory_name; });
    return Object.entries(seen).map(([id, name]) => ({ id, name }));
  }, [records]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (q && !String(r.order_number || "").toLowerCase().includes(q.toLowerCase())) return false;
      if (date && !String(r.created_at || "").startsWith(date)) return false;
      if (actor !== "all" && r.actor_id !== actor) return false;
      if (factory !== "all" && r.factory_id !== factory) return false;
      if (action !== "all" && r.action !== action) return false;
      return true;
    });
  }, [records, q, date, actor, factory, action]);

  const hasFilters = q || date || actor !== "all" || factory !== "all" || action !== "all";
  const clearFilters = () => { setQ(""); setDate(""); setActor("all"); setFactory("all"); setAction("all"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNav />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div>
          <h1 className="font-heading flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-900">
            <HistoryIcon className="h-8 w-8 text-blue-600" /> History
          </h1>
          <p className="mt-1 text-lg text-slate-500">Complete audit trail of all order activity (newest first)</p>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input data-testid="history-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order number…" className={cn(inputCls, "pl-10")} />
            </div>
            <input data-testid="history-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            <Select value={actor} onValueChange={setActor}>
              <SelectTrigger data-testid="history-user-filter" className="h-11 text-base"><SelectValue placeholder="User" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {actors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="history-action-filter" className="h-11 text-base"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                {ACTION_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a === "all" ? "All actions" : ACTION_META[a].label}</SelectItem>)}
              </SelectContent>
            </Select>
            {showFactoryFilter && (
              <Select value={factory} onValueChange={setFactory}>
                <SelectTrigger data-testid="history-factory-filter" className="h-11 text-base"><SelectValue placeholder="Factory" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All factories</SelectItem>
                  {factories.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          {hasFilters && (
            <button data-testid="history-clear-filters" onClick={clearFilters} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline">
              <X className="h-4 w-4" /> Clear filters
            </button>
          )}
        </div>

        {/* Records */}
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-lg text-slate-400">
            No history records found.
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {filtered.map((r) => {
              const meta = ACTION_META[r.action] || ACTION_META.edited;
              return (
                <button
                  key={r.id}
                  data-testid={`history-record-${r.id}`}
                  onClick={() => navigate(`/orders/${r.order_id}`)}
                  className="mf-fade-in flex w-full items-start gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
                >
                  <div className="hidden w-28 shrink-0 sm:block">
                    <p className="text-base font-bold text-slate-800">{fmtTime(r.created_at)}</p>
                    <p className="text-sm text-slate-400">{fmtDay(r.created_at)}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide", meta.badge)}>
                        {meta.label}
                      </span>
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-sm font-semibold text-slate-600">Order {r.order_number}</span>
                    </div>
                    <p className="mt-1.5 text-base text-slate-800">
                      <span className="font-bold">{r.actor_name}</span>{" "}
                      <span className="text-slate-500">({(r.actor_role || "").replace("_", " ")})</span>{" "}
                      {r.detail}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-400 sm:hidden">{fmtTime(r.created_at)} · {fmtDay(r.created_at)} · {r.factory_name}</p>
                    <p className="mt-0.5 hidden text-sm text-slate-400 sm:block">{r.factory_name}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Load older history (7-day windows, fetched server-side) */}
        {!loading && hasOlder && (
          <div className="mt-6 flex justify-center">
            <Button
              data-testid="history-load-previous-week"
              variant="outline"
              onClick={loadPrevious}
              disabled={loadingMore}
              className="h-11 px-6 text-base font-semibold"
            >
              {loadingMore ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</> : "Load Previous Week"}
            </Button>
          </div>
        )}
        {!loading && !hasOlder && records.length > 0 && (
          <p data-testid="history-no-older" className="mt-6 text-center text-sm text-slate-400">No older history to load.</p>
        )}
      </main>
    </div>
  );
}
